// ============================================================================
// MarketClientProfile — экран профиля клиента витрины маркетплейса.
//
// Состав (мобильный, одна колонка, без таблиц/окон платформы):
//   • контакты: имя, телефон + подтверждение, email, город, район;
//   • «Мои адреса» — адресная книга (user_settings) для быстрого выбора в заявке;
//   • уведомления и чаты (существующие settingsStore-ключи);
//   • вход в «Кабинет мастера», если профиль мастера есть.
//
// Данные: существующие auth/profile API (EntityService) + settingsStore.
// Чего нет в поставке (отдельный реквизит района, справочник адресов клиента,
// связь mkt_clients с пользователем) — НЕ подменяется моком: район и адреса
// живут в персональных настройках, профиль — в сущности пользователя.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { logErrorToJournal, logSwallowed } from '@/lib/errors';
import {
  loadMarketClientProfile,
  toMarketProfileFailure,
  type MarketClientProfile as MarketClientProfileData,
} from '@/lib/marketProfile';
import { loadMyMarketPhoneGate, type MarketPhoneGate } from '@/lib/market';
import { MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketPhoneStatus } from '@/components/market/MarketPhoneStatus';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { MarketContactCard } from '@/components/profile/market/MarketContactCard';
import { MarketAddressBook } from '@/components/profile/market/MarketAddressBook';
import { MarketNotifySettingsCard } from '@/components/profile/market/MarketNotifySettingsCard';
import { MarketMasterCabinetCard } from '@/components/profile/market/MarketMasterCabinetCard';

interface MarketClientProfileProps {
  /** 'master' — в базе есть профиль мастера (mkt_masters.entity_id). */
  role: 'client' | 'master';
  onBack: () => void;
  onOpenMasterFeed: () => void;
  onOpenWallet: () => void;
  /** Зона обслуживания мастера (кабинет мастера). */
  onOpenArea: () => void;
}

export function MarketClientProfile({
  role,
  onBack,
  onOpenMasterFeed,
  onOpenWallet,
  onOpenArea,
}: MarketClientProfileProps) {
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const [profile, setProfile] = useState<MarketClientProfileData | null>(null);
  const [phoneGate, setPhoneGate] = useState<MarketPhoneGate | null>(null);
  const [failure, setFailure] = useState<ReturnType<typeof toMarketProfileFailure> | null>(null);
  const [refreshError, setRefreshError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const profileRef = useRef<MarketClientProfileData | null>(null);

  const load = useCallback(async () => {
    setFailure(null);
    setRefreshError('');
    setLoading(profileRef.current === null);
    try {
      if (!userId) throw new Error('Не определён текущий пользователь — войдите заново');
      const next = await loadMarketClientProfile(userId);
      profileRef.current = next;
      setProfile(next);
      // Гейт телефона — по серверным меткам последней заявки (SSoT). Ошибка
      // чтения не блокирует профиль: статус остаётся неизвестным.
      try {
        setPhoneGate(await loadMyMarketPhoneGate(userId, next.phone));
      } catch (e) {
        logSwallowed(e, 'Профиль маркетплейса: статус телефона для заявок');
        setPhoneGate(null);
      }
    } catch (e) {
      logErrorToJournal(e, 'Профиль маркетплейса: загрузка');
      if (profileRef.current === null) setFailure(toMarketProfileFailure(e));
      else setRefreshError(toMarketProfileFailure(e).message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);

  if (loading && !profile) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title="Профиль" subtitle="Клиент маркетплейса" onBack={onBack} />
        <div className="flex-1 overflow-y-auto">
          <MarketLoading text="Загружаем профиль…" />
        </div>
      </div>
    );
  }

  if (failure && !profile) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title="Профиль" subtitle="Клиент маркетплейса" onBack={onBack} />
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-2xl p-4 sm:p-6">
            <MarketError failure={failure} onRetry={reload} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title="Профиль"
        subtitle={role === 'master' ? 'Клиент и мастер маркетплейса' : 'Клиент маркетплейса'}
        onBack={onBack}
        action={(
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg px-2 text-xs"
            onClick={reload}
            disabled={loading}
            aria-label="Обновить профиль"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-10 sm:p-6">
          {refreshError && (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs text-destructive">
              Не удалось обновить профиль: {refreshError}
            </div>
          )}

          {role === 'master' && (
            <MarketMasterCabinetCard
              onOpenFeed={onOpenMasterFeed}
              onOpenWallet={onOpenWallet}
              onOpenArea={onOpenArea}
            />
          )}

          {phoneGate && (
            <MarketPhoneStatus
              state={phoneGate.state}
              audience="client"
              hint={phoneGate.hint || undefined}
            />
          )}

          {profile && (
            <MarketContactCard
              profile={profile}
              onSaved={(next) => {
                profileRef.current = next;
                setProfile(next);
              }}
              onReload={reload}
            />
          )}

          <MarketAddressBook />

          <MarketNotifySettingsCard />

          <div className="rounded-2xl border border-dashed bg-card/50 px-4 py-3 text-[10px] leading-5 text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <UserRound className="h-3.5 w-3.5" />
              Данные профиля
            </span>
            <div className="mt-1">
              Телефон и город хранятся в профиле пользователя, район и адреса — в ваших настройках.
              Профиль клиента не связан с справочником заказчиков маркетплейса — данные заявки
              заполняются отдельно.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
